import type {Property} from "@/src/modules/properties/types";

import type {Me} from "../types";
import type {ResidentMembership} from "../types/resident-membership";

export function getPropertyMemberships(property: Property): ResidentMembership[] {
  return property.memberships ?? [];
}

export function sortResidentProperties(properties: Property[]): Property[] {
  return [...properties].toSorted((a, b) =>
    a.name.localeCompare(b.name, undefined, {sensitivity: "base"}),
  );
}

export function findMembershipInProperty(
  property: Property,
  householdId: number | null | undefined,
): ResidentMembership | null {
  if (householdId == null) return null;
  return (
    getPropertyMemberships(property).find(
      (m) => String(m.house_hold_id) === String(householdId),
    ) ?? null
  );
}

export function findResidentPropertyById(
  me: Pick<Me, "properties">,
  propertyId: number | null | undefined,
): Property | null {
  if (propertyId == null) return null;
  return me.properties.find((p) => String(p.id) === String(propertyId)) ?? null;
}

export function resolveMembershipInMe(
  me: Pick<Me, "properties">,
  propertyId: number,
  householdId: number,
): {property: Property; membership: ResidentMembership} | null {
  const property = findResidentPropertyById(me, propertyId);
  if (!property) return null;
  const membership = findMembershipInProperty(property, householdId);
  if (!membership) return null;
  return {property, membership};
}

export function getDefaultResidentContext(me: Pick<Me, "properties">): {
  property: Property;
  membership: ResidentMembership;
} | null {
  return getFirstAvailableResidentContext(me);
}

/** First property (by name) with a membership, optionally skipping a forbidden household. */
export function getFirstAvailableResidentContext(
  me: Pick<Me, "properties">,
  excludeHouseholdId?: number | null,
): {property: Property; membership: ResidentMembership} | null {
  const sorted = sortResidentProperties(me.properties);
  for (const property of sorted) {
    const memberships = getPropertyMemberships(property);
    const membership =
      excludeHouseholdId == null
        ? memberships[0]
        : memberships.find(
            (m) => String(m.house_hold_id) !== String(excludeHouseholdId),
          );
    if (membership) return {property, membership};
  }
  return null;
}

export function getResidentMembershipRoleLabel(
  membership: Pick<ResidentMembership, "is_owner" | "is_occupant">,
): string {
  if (membership.is_owner && membership.is_occupant) return "Owner-occupant";
  if (membership.is_owner) return "Landlord";
  if (membership.is_occupant) return "Tenant";
  return "Member";
}

export function residentNeedsContextSwitcher(me: Pick<Me, "properties">): boolean {
  if (me.properties.length > 1) return true;
  const memberships = getPropertyMemberships(me.properties[0] ?? ({} as Property));
  return memberships.length > 1;
}

export function residentPropertyNeedsSwitcher(property: Property): boolean {
  return getPropertyMemberships(property).length > 1;
}
